import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SRNStatusBadge } from "./AdminSRNLedger";

const KIND_LABEL = {
  rfq: "Quote Request", quote: "Quote Request", kyc: "KYC Filing",
  refund: "Refund Claim", indirect_commission: "Indirect Commission",
  concierge: "Support Message", order: "Order",
  motion: "Board Motion", inquiry: "Contact Inquiry",
  rectification: "Rectification Mandate",
};
const KIND_COLOR = {
  rfq: "#C9A961", quote: "#C9A961", kyc: "#2FB999",
  refund: "#C0392B", indirect_commission: "#C0392B",
  concierge: "#B87333", order: "#C9A961",
  motion: "#A6821E", inquiry: "#8A8A82",
};
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

export function MySRNs() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-srns")
      .then((r) => setRows(r.data.rows || []))
      .catch(() => toast.error("Could not load your SRN timeline."))
      .finally(() => setLoading(false));
  }, [api]);

  const filtered = kind ? rows.filter((r) => r.kind === kind) : rows;

  return (
    <section data-testid="my-srns" className="border border-white/10 rounded-sm p-6 md:p-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
            · Vinci Master Ledger · Your Case Files ·
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">Service Request Numbers</h3>
          <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl leading-relaxed">
            Every interaction you file with us — quote requests, KYC, claims,
            concierge notes — is stamped with a Vinci SRN so you can thread every
            update in one place.
          </p>
        </div>
        <select
          data-testid="my-srns-filter"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded-full border border-white/20 bg-transparent px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#C9A961] outline-none"
        >
          <option value="">All kinds</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
      </header>

      <div className="mt-6">
        {loading ? (
          <p className="font-body text-sm text-[#8A8A82]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="font-body text-sm text-[#8A8A82]" data-testid="my-srns-empty">
            No case files filed under this filter yet.
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {filtered.map((r) => (
              <li key={r.srn} className="py-3 flex items-center justify-between gap-4 flex-wrap"
                data-testid={`my-srn-row-${r.srn}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="grid place-items-center w-8 h-8 rounded-full shrink-0"
                    style={{ background: (KIND_COLOR[r.kind] || "#8A8A82") + "22",
                                color: KIND_COLOR[r.kind] || "#8A8A82" }}
                  >
                    <FileText size={13} />
                  </span>
                  <div className="min-w-0">
                    <div style={{ fontFamily: MONO }} className="text-[#C9A961] text-sm tracking-[0.06em] truncate">
                      {r.srn}
                    </div>
                    <div className="font-body text-xs text-[#8A8A82] mt-0.5">
                      {KIND_LABEL[r.kind] || r.kind} · {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <SRNStatusBadge status={r.status || "open"} testid={`my-srn-status-${r.srn}`} />
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(r.srn); toast.success("SRN copied"); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#C9A961] hover:text-[#C9A961]"
                >
                  <Copy size={11} /> Copy
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
